import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../../store/store';
import { columnGapPx, marginsObject, pageHeight, pageWidth } from '../../constants/gridConfig';
import { defaultConfiguration, ItemTypes, MASKS_TYPE, viewerTypes } from '../../constants/constants';
import { PreviewProvider } from '../../contexts/PreviewContext';
import { isWatermarkShown } from '../../utils/watermark';
import { displayTitleIndex, getTitlesConfig, numberedTitleLevels } from '../../utils/titles';
import Container from '../Container/Container';
import ConfigPreviewFrame from '../common/ConfigPreviewFrame';
import Title from '../Title';
import { SummaryTitleItem } from '../Summary/Summary';
import { selectComponent } from '../common/Box';
import WatermarkOverlay from '../common/WatermarkOverlay';
import '../RightSide/configurationModal/DocumentTab.less';
import AccordionPanelPreview from '../RightSide/configurationModal/AccordionPanelPreview';
import InfoCardPreview from '../RightSide/configurationModal/InfoCardPreview';
import {
  mergeConfigurationAccordionPanelStyles,
  mergeConfigurationInfoCardStyles,
} from '../../utils/styles';
import { DEFAULT_FONT_FAMILY, DEFAULT_TOP } from '../RightSide/configurationModal/pageTab';
import {
  previewCheckboxBox,
  previewEditorState,
  previewImageBox,
  previewLongText,
  previewParagraph,
  previewParagraphAndList,
  previewSelectorBox,
  previewShapeBox,
  previewSummaryTitles,
  previewTableBox,
  previewTextBox,
  previewTitleBox,
  previewTitleBoxes,
} from './previewFixtures';

// `dimmed` : contenu de contexte, sans rapport avec le réglage en cours. Un
// fond blanc opaque le recouvre, barré de traits gris comme le squelette d'antd :
// on voit la place qu'il prend, pas ce qu'il dit, et l'œil va sur ce que
// l'onglet règle vraiment. (Un flou ou une transparence laissent encore lire.)
const SKELETON_BAR = 8;
const SKELETON_STEP = 18;
const DimmedCover = () => (
  <div
    data-dimmed-cover
    style={{
      position: 'absolute',
      inset: 0,
      // Au-dessus de tout ce que la box empile (z-index, position) : le
      // conteneur isole son empilement (cf. Row).
      zIndex: 9999,
      backgroundColor: '#fff',
      backgroundImage: `repeating-linear-gradient(to bottom, transparent 0, transparent ${(SKELETON_STEP - SKELETON_BAR) / 2}px, #e4e6ea ${(SKELETON_STEP - SKELETON_BAR) / 2}px, #e4e6ea ${(SKELETON_STEP + SKELETON_BAR) / 2}px, transparent ${(SKELETON_STEP + SKELETON_BAR) / 2}px, transparent ${SKELETON_STEP}px)`,
    }}
  />
);

// Un container du document : son contenu tient entre les marges gauche et
// droite de la page (cf. Container).
const Row = ({ layout, justify = 'flex-start', gap, dimmed, children }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: justify,
      gap,
      paddingLeft: layout.margins.left,
      paddingRight: layout.margins.right,
    }}
  >
    {dimmed ? (
      <div style={{ position: 'relative', isolation: 'isolate' }}>
        {children}
        <DimmedCover />
      </div>
    ) : children}
  </div>
);

// Comme dans le document : titre aligné à droite (positions 'end'), sa largeur
// réduite par le décalage de son niveau (marges + décalage des titres, onglet
// Marges).
const TitleRow = ({ layout, box, dimmed }) => {
  const gridConfiguration = useStore(({ gridConfiguration }) => gridConfiguration);
  return (
    <Row layout={layout} justify='flex-end' dimmed={dimmed}>
      <Title box={{ ...box, width: gridConfiguration[box.type]?.width ?? layout.contentWidth }} drawMode />
    </Row>
  );
};

const TextRow = ({ layout, id, editorState, textStyleId, dimmed }) => {
  const gridConfiguration = useStore(({ gridConfiguration }) => gridConfiguration);
  const box = useMemo(
    () => previewTextBox(gridConfiguration, id, editorState, layout.contentWidth, textStyleId),
    [gridConfiguration, id, editorState, layout.contentWidth, textStyleId]
  );
  return <Row layout={layout} dimmed={dimmed}>{selectComponent(box, false, false)}</Row>;
};

const CheckboxRow = ({ layout, id, checkboxStyleId = 0, dimmed }) => {
  const gridConfiguration = useStore(({ gridConfiguration }) => gridConfiguration);
  const box = useMemo(
    () => previewCheckboxBox(gridConfiguration, id, checkboxStyleId, layout.contentWidth),
    [gridConfiguration, id, checkboxStyleId, layout.contentWidth]
  );
  return <Row layout={layout} dimmed={dimmed}>{selectComponent(box, false, false)}</Row>;
};

const SelectorRow = ({ layout, id, answers, selectorStyleId = 0, dimmed }) => {
  const gridConfiguration = useStore(({ gridConfiguration }) => gridConfiguration);
  const box = useMemo(
    () => previewSelectorBox(gridConfiguration, id, selectorStyleId, layout.contentWidth, answers),
    [gridConfiguration, id, selectorStyleId, layout.contentWidth, answers]
  );
  return <Row layout={layout} dimmed={dimmed}>{selectComponent(box, false, false)}</Row>;
};

// Style de tableau : celui choisi dans la liste de l'onglet Tableaux, sinon le
// premier du document.
const TableRow = ({ layout, tableStyleId, dimmed }) => {
  const gridConfiguration = useStore(({ gridConfiguration }) => gridConfiguration);
  const defaultTableStyleId = useStore(({ configuration }) => configuration.tableStyles[0]?.id);
  const table = useMemo(
    () => previewTableBox(gridConfiguration, tableStyleId ?? defaultTableStyleId, layout.contentWidth),
    [gridConfiguration, tableStyleId, defaultTableStyleId, layout.contentWidth]
  );
  return <Row layout={layout} dimmed={dimmed}>{selectComponent(table, false, false)}</Row>;
};

const TITLES_SELECTOR_ANSWERS = ['Livraison sur site'];
const titlesSelectorQuestion = previewEditorState('Mode de livraison');
const titlesCheckboxIntro = previewEditorState('Cochez les prestations à inclure :');

// Un titre par niveau, chacun suivi de ce qu'on trouve sous un titre dans le
// document (texte, cases à cocher, choix, tableau), pour juger les titres en
// contexte.
const TitlesPreview = ({ layout }) => {
  const [title1, title2, title3, title4] = previewTitleBoxes;
  return (
    <>
      <TitleRow layout={layout} box={title1} />
      <TextRow layout={layout} id='titles-text' editorState={previewParagraph} dimmed />
      <TitleRow layout={layout} box={title2} />
      <TextRow layout={layout} id='titles-checkbox-intro' editorState={titlesCheckboxIntro} dimmed />
      <CheckboxRow layout={layout} id='titles-checkbox' dimmed />
      <TitleRow layout={layout} box={title3} />
      <TextRow layout={layout} id='titles-selector-question' editorState={titlesSelectorQuestion} dimmed />
      <SelectorRow layout={layout} id='titles-selector' answers={TITLES_SELECTOR_ANSWERS} dimmed />
      <TitleRow layout={layout} box={title4} />
      <TableRow layout={layout} dimmed />
    </>
  );
};

const marginsTitle1 = previewTitleBox('margins-title-1', ItemTypes.TITLE, [1], 'Titre de niveau 1');
const marginsTitle2 = previewTitleBox('margins-title-2', ItemTypes.TITLE_2, [1, 1], 'Titre de niveau 2');

// Un peu de chaque : titres (décalage), texte, une ligne à deux colonnes
// (image, forme), pour voir sur quoi portent marges et espacement des lignes.
const MarginsPreview = ({ layout }) => {
  const gridConfiguration = useStore(({ gridConfiguration }) => gridConfiguration);
  const image = useMemo(() => previewImageBox(gridConfiguration, 'margins-image'), [gridConfiguration]);
  const shape = useMemo(() => previewShapeBox(gridConfiguration, 'margins-shape'), [gridConfiguration]);
  const columnWidth = (layout.contentWidth - columnGapPx) / 2;
  const column = { width: columnWidth, display: 'flex', justifyContent: 'center' };

  return (
    <>
      <TitleRow layout={layout} box={marginsTitle1} />
      <TextRow layout={layout} id='margins-text-1' editorState={previewParagraph} />
      <TitleRow layout={layout} box={marginsTitle2} />
      <TextRow layout={layout} id='margins-text-2' editorState={previewParagraph} />
      <Row layout={layout} gap={columnGapPx}>
        <div style={column}>{selectComponent(image, false, false)}</div>
        <div style={column}>{selectComponent(shape, false, false)}</div>
      </Row>
    </>
  );
};

// Repères des marges gauche et droite, sur toute la hauteur de la page.
const MarginGuides = ({ layout }) =>
  [layout.margins.left, layout.width - layout.margins.right].map((left) => (
    <div
      key={left}
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left,
        borderLeft: '1px dashed rgba(64, 150, 255, 0.8)',
        zIndex: 1,
      }}
    />
  ));

const fontTitle = previewTitleBox('font-title', ItemTypes.TITLE, [1], 'Titre de niveau 1');
const FONT_SELECTOR_ANSWERS = ['Installation', 'Formation'];
const fontSelectorQuestion = previewEditorState('Prestations retenues');

// Titre, texte et puces, puis les autres boxes qui portent la police du
// document : cases à cocher, choix, tableau.
const FontPreview = ({ layout }) => (
  <>
    <TitleRow layout={layout} box={fontTitle} dimmed />
    <TextRow layout={layout} id='font-text' editorState={previewParagraphAndList} />
    <CheckboxRow layout={layout} id='font-checkbox' />
    <TextRow layout={layout} id='font-selector-question' editorState={fontSelectorQuestion} />
    <SelectorRow layout={layout} id='font-selector' answers={FONT_SELECTOR_ANSWERS} />
    <TableRow layout={layout} />
  </>
);

const tablesTitle = previewTitleBox('tables-title', ItemTypes.TITLE, [1], 'Titre de niveau 1');

// Style sélectionné dans la liste de l'onglet (état local de l'onglet). Le
// tableau est posé dans une page : un titre et du texte autour, en squelette.
const TablesPreview = ({ layout, values }) => (
  <>
    <TitleRow layout={layout} box={tablesTitle} dimmed />
    <TextRow layout={layout} id='tables-text-before' editorState={previewParagraph} dimmed />
    <TableRow layout={layout} tableStyleId={values.tableStyleId} />
    <TextRow layout={layout} id='tables-text-after' editorState={previewParagraph} dimmed />
  </>
);

const summaryContextTitle1 = previewTitleBox('summary-context-title-1', ItemTypes.TITLE, [1], 'Présentation');
const summaryContextTitle2 = previewTitleBox('summary-context-title-2', ItemTypes.TITLE_2, [1, 1], 'Notre entreprise');

// Lignes du vrai sommaire (SummaryTitleItem), sur des titres factices : les
// niveaux masqués sautent et les numéros suivent la numérotation des titres,
// comme dans le document (cf. getDisplayableTitles). Dessous, le début du
// document en squelette, pour juger le sommaire en contexte.
const SummaryPreview = ({ layout }) => {
  const configuration = useStore(({ configuration }) => configuration);
  const summaryConfiguration = useStore(({ summaryConfiguration }) => summaryConfiguration);
  const summaryWidth = useStore(({ gridConfiguration }) => gridConfiguration.summary?.width);
  const titles = previewSummaryTitles
    .filter((title) => summaryConfiguration[title.type].visibility)
    .map((title) => ({
      ...title,
      indexTitle: displayTitleIndex(
        title.indexTitle,
        configuration.separatorTitle,
        getTitlesConfig(configuration),
        numberedTitleLevels(configuration),
        configuration.finalSeparatorTitle
      ),
    }));

  return (
    <>
      <Row layout={layout} justify='flex-end'>
        <div style={{ width: summaryWidth ?? layout.contentWidth }}>
          {titles.map((title, idx) => (
            <SummaryTitleItem
              key={title.id}
              boxId='__preview-summary'
              title={title}
              style={summaryConfiguration[title.type]}
              isFirst={idx === 0}
              isLast={idx === titles.length - 1}
              titleConfig={configuration[title.type]}
              indexStyleFromTitles={summaryConfiguration.indexStyleFromTitles}
              indentation={summaryConfiguration.indentation}
              pageNumber={title.pageNumber}
            />
          ))}
        </div>
      </Row>
      <TitleRow layout={layout} box={summaryContextTitle1} dimmed />
      <TextRow layout={layout} id='summary-text-1' editorState={previewParagraph} dimmed />
      <TitleRow layout={layout} box={summaryContextTitle2} dimmed />
      <TextRow layout={layout} id='summary-text-2' editorState={previewParagraph} dimmed />
      <TableRow layout={layout} dimmed />
    </>
  );
};

const basicTitle = previewTitleBox('basic-title', ItemTypes.TITLE, [1], 'Titre de niveau 1');

// Contenu quelconque, pour un réglage qui ne porte pas sur lui (zone de saut
// de page, sans objet en paysage) : la page ne paraît pas vide.
const BasicPreview = ({ layout }) => (
  <>
    <TitleRow layout={layout} box={basicTitle} />
    <TextRow layout={layout} id='basic-text-1' editorState={previewParagraph} />
    <TextRow layout={layout} id='basic-text-2' editorState={previewParagraph} />
  </>
);

const pageNumberTitle1 = previewTitleBox('page-number-title-1', ItemTypes.TITLE, [1], 'Titre de niveau 1');
const pageNumberTitle2 = previewTitleBox('page-number-title-2', ItemTypes.TITLE_2, [1, 1], 'Titre de niveau 2');

// Page remplie en squelette : seul le numéro de page (PageNumberOverlay) reste
// lisible.
const PageNumberPreview = ({ layout }) => (
  <>
    <TitleRow layout={layout} box={pageNumberTitle1} dimmed />
    <TextRow layout={layout} id='page-number-text-1' editorState={previewParagraph} dimmed />
    <TitleRow layout={layout} box={pageNumberTitle2} dimmed />
    <TextRow layout={layout} id='page-number-text-2' editorState={previewParagraph} dimmed />
    <TableRow layout={layout} dimmed />
    <TextRow layout={layout} id='page-number-text-3' editorState={previewParagraph} dimmed />
  </>
);

const watermarkTitle2 = previewTitleBox('watermark-title-2', ItemTypes.TITLE_2, [1, 2], 'Titre de niveau 2');

// Page bien remplie (titres, texte, image, forme), sans tableau : le filigrane
// passe sous le contenu, le fond blanc des cellules le masquerait.
const WatermarkPreview = ({ layout }) => (
  <>
    <MarginsPreview layout={layout} />
    <TitleRow layout={layout} box={watermarkTitle2} />
    <TextRow layout={layout} id='watermark-text-1' editorState={previewParagraph} />
    <TextRow layout={layout} id='watermark-text-2' editorState={previewParagraph} />
  </>
);

// Même rendu que l'aperçu du pied de page de l'onglet (pageTab), placé cette
// fois par rapport aux bords de la page.
const PageNumberOverlay = () => {
  const configuration = useStore(({ configuration }) => configuration);
  const pageNumberStyle = configuration.pageNumberStyle;
  if (pageNumberStyle.format === 'empty') return null;

  const isInHeader = pageNumberStyle.placement === 'header';
  return (
    <span
      style={{
        position: 'absolute',
        fontFamily: pageNumberStyle.fontFamily || DEFAULT_FONT_FAMILY,
        right: pageNumberStyle.position.right,
        ...(isInHeader
          ? { top: pageNumberStyle.position.top ?? DEFAULT_TOP }
          : { bottom: pageNumberStyle.position.bottom }),
        fontSize: pageNumberStyle.size,
        color: pageNumberStyle.color?.style ?? configuration.baseFontColor.style,
        fontStyle: pageNumberStyle.fontStyle,
        fontWeight: pageNumberStyle.fontWeight,
        textDecoration: pageNumberStyle.textDecoration,
        lineHeight: 1,
        zIndex: 999,
      }}
    >
      1{pageNumberStyle.format === 'total' ? '/3' : ''}
    </span>
  );
};

const WatermarkPreviewOverlay = ({ layout }) => {
  // Réglages par défaut tant que la configuration n'en a pas (cf. watermarkTab).
  const watermark = useStore(({ configuration }) => configuration.watermark) ?? defaultConfiguration.watermark;
  if (!isWatermarkShown(watermark)) return null;
  return <WatermarkOverlay watermark={watermark} width={layout.width} height={layout.height} />;
};

// Onglet Styles des éléments : une page par sous-onglet, l'élément au style
// sélectionné dans la liste (état local du sous-onglet), dans un contenu qui
// lui ressemble. Un sous-onglet pas encore monté : style par défaut (id 0).

const elementsTitle = (key, text) => previewTitleBox(`elements-title-${key}`, ItemTypes.TITLE, [1], text);
const ELEMENT_TITLES = {
  'text': elementsTitle('text', 'Contexte du projet'),
  'checkbox': elementsTitle('checkbox', 'Prestations souhaitées'),
  'selector': elementsTitle('selector', 'Modalités de la prestation'),
  'accordion': elementsTitle('accordion', 'Questions fréquentes'),
  'info-card': elementsTitle('info-card', 'Nos chiffres clés'),
};

const styledTextSample = previewEditorState(
  'Ce champ texte porte le style sélectionné : fond, bordures, marges ' +
  'intérieures, police, taille et alignement du texte. Les paragraphes autour ' +
  'gardent le style par défaut du document.'
);

const TextFieldPreview = ({ layout, values }) => (
  <>
    <TitleRow layout={layout} box={ELEMENT_TITLES['text']} />
    <TextRow layout={layout} id='elements-text-before' editorState={previewParagraph} />
    <TextRow layout={layout} id='elements-text-styled' editorState={styledTextSample} textStyleId={values.textStyleId ?? 0} />
    <TextRow layout={layout} id='elements-text-after' editorState={previewParagraph} />
  </>
);

const introCheckboxes = previewEditorState('Cochez les prestations à inclure dans la proposition :');

const CheckboxPreview = ({ layout, values }) => (
  <>
    <TitleRow layout={layout} box={ELEMENT_TITLES['checkbox']} />
    <TextRow layout={layout} id='elements-checkbox-intro' editorState={introCheckboxes} />
    <CheckboxRow layout={layout} id='elements-checkbox' checkboxStyleId={values.checkboxStyleId} />
    <TextRow layout={layout} id='elements-checkbox-after' editorState={previewParagraph} />
  </>
);

const SELECTOR_QUESTIONS = [
  ['elements-selector-single', 'Mode de livraison', ['Livraison sur site']],
  ['elements-selector-multiple', 'Prestations retenues', ['Installation', 'Formation', 'Maintenance']],
].map(([key, question, answers]) => ({
  key,
  question: previewEditorState(question),
  answers,
}));

const SelectorPreview = ({ layout, values }) => (
  <>
    <TitleRow layout={layout} box={ELEMENT_TITLES['selector']} />
    <TextRow layout={layout} id='elements-selector-intro' editorState={previewParagraph} />
    {SELECTOR_QUESTIONS.map(({ key, question, answers }) => (
      <React.Fragment key={key}>
        <TextRow layout={layout} id={`${key}-question`} editorState={question} />
        <SelectorRow layout={layout} id={key} answers={answers} selectorStyleId={values.selectorStyleId} />
      </React.Fragment>
    ))}
  </>
);

const ACCORDION_QUESTIONS = [
  ['Quels sont vos délais ?', 'Nous livrons généralement sous 2 à 4 semaines.'],
  ['Proposez-vous une formation ?', 'Oui, une demi-journée de prise en main est incluse.'],
  ['Comment se passe la maintenance ?', 'Un interlocuteur dédié répond sous 24 h ouvrées.'],
];

const AccordionPanelPagePreview = ({ layout, values }) => {
  const configuration = useStore(({ configuration }) => configuration);
  const tmp = useStore(({ tmp }) => tmp);
  const styles = mergeConfigurationAccordionPanelStyles(configuration, tmp);
  const style = styles.find(({ id }) => id === values.accordionPanelStyleId) ?? styles[0];
  return (
    <>
      <TitleRow layout={layout} box={ELEMENT_TITLES['accordion']} />
      <TextRow layout={layout} id='elements-accordion-intro' editorState={previewParagraph} />
      <Row layout={layout}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {ACCORDION_QUESTIONS.map(([question, answer]) => (
            <AccordionPanelPreview
              key={question}
              style={style}
              width={layout.contentWidth}
              question={question}
              answer={answer}
            />
          ))}
        </div>
      </Row>
    </>
  );
};

// Trois cartes côte à côte, comme une rangée de chiffres clés. Un modèle de la
// galerie emporte son texte d'exemple (témoignage, membre d'équipe…) : il est
// alors repris sur les trois cartes, qui garderaient sinon un chiffre hors sujet.
const INFO_CARD_SAMPLES = [
  { value: '98 %', description: 'Clients satisfaits' },
  { value: '15 ans', description: "D'expérience" },
  { value: '+ 200', description: 'Projets livrés' },
];

const InfoCardPagePreview = ({ layout, values }) => {
  const configuration = useStore(({ configuration }) => configuration);
  const tmp = useStore(({ tmp }) => tmp);
  const styles = mergeConfigurationInfoCardStyles(configuration, tmp);
  const style = styles.find(({ id }) => id === values.infoCardStyleId) ?? styles[0];
  const cardWidth = (layout.contentWidth - 2 * columnGapPx) / 3;
  return (
    <>
      <TitleRow layout={layout} box={ELEMENT_TITLES['info-card']} />
      <TextRow layout={layout} id='elements-info-card-intro' editorState={previewParagraph} />
      <Row layout={layout} gap={columnGapPx}>
        {INFO_CARD_SAMPLES.map((sample) => (
          <InfoCardPreview
            key={sample.value}
            style={style}
            width={cardWidth}
            sample={style?.sample ?? sample}
          />
        ))}
      </Row>
      <TextRow layout={layout} id='elements-info-card-after' editorState={previewParagraph} />
    </>
  );
};

const ELEMENT_PREVIEWS = {
  [ItemTypes.TEXT_VARIABLE]: TextFieldPreview,
  [ItemTypes.CHECKBOXS_VARIABLE]: CheckboxPreview,
  [ItemTypes.SELECTOR_VARIABLE]: SelectorPreview,
  [ItemTypes.ACCORDION_PANEL]: AccordionPanelPagePreview,
  [ItemTypes.INFO_CARD]: InfoCardPagePreview,
};

const ElementStylesPreview = ({ layout, values }) => {
  const Preview = ELEMENT_PREVIEWS[values.elementType] ?? TextFieldPreview;
  return <Preview layout={layout} values={values} />;
};

// Onglet Document : zone de saut de page des titres (dmz), en bas du corps de
// page. Un titre posé juste au-dessus, puis du texte jusqu'au pied de page,
// coupé à la dernière ligne entière : la zone rayée recouvre ce texte.
const dmzTitle = previewTitleBox('dmz-title', ItemTypes.TITLE, [1], 'Mon titre');

const DmzPreview = ({ layout }) => {
  const configuration = useStore(({ configuration }) => configuration);
  const landscape = useStore(({ landscape }) => landscape);
  const textRef = useRef();
  const [hiddenHeight, setHiddenHeight] = useState(0);
  const textHeight = Math.max(configuration.dmz - configuration.lineSpacing - 10, 0);

  useLayoutEffect(() => {
    if (!textRef.current) return;
    const lineHeight = parseFloat(window.getComputedStyle(textRef.current).lineHeight);
    if (!lineHeight) return;
    setHiddenHeight(textHeight - Math.trunc(textHeight / lineHeight) * lineHeight);
  }, [textHeight]);

  // Pas de zone de saut de page en paysage (cf. DocumentTab).
  if (landscape) return <BasicPreview layout={layout} />;

  return (
    <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: configuration.lineSpacing, overflow: 'hidden' }}>
        <TextRow layout={layout} id='dmz-text-before' editorState={previewParagraph} />
        <TitleRow layout={layout} box={dmzTitle} />
        <div ref={textRef} style={{ height: textHeight, overflow: 'hidden' }}>
          <TextRow layout={layout} id='dmz-text' editorState={previewLongText} />
        </div>
      </div>
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: hiddenHeight, backgroundColor: 'white', zIndex: 1 }} />
      <div className='zone-dmz' style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: configuration.dmz, zIndex: 2 }} />
    </div>
  );
};

// Contenu de la page par onglet (clé de ConfigDrawer), et ce qui se pose
// par-dessus la page entière. Onglet absent : pas d'aperçu.
const PREVIEWS = {
  Titres: { Body: TitlesPreview },
  Marges: { Body: MarginsPreview, Overlay: MarginGuides },
  Police: { Body: FontPreview },
  Sommaire: { Body: SummaryPreview },
  Tableaux: { Body: TablesPreview },
  'N° page': { Body: PageNumberPreview, Overlay: PageNumberOverlay },
  watermark: { Body: WatermarkPreview, Overlay: WatermarkPreviewOverlay },
  variables: { Body: ElementStylesPreview },
  document: { Body: DmzPreview },
};

// Aperçu d'une page factice dans la zone grise à droite du Drawer de
// configuration. Pur affichage : lit la configuration, n'écrit rien. `values` :
// réglages en cours d'essai partagés par l'onglet (cf. ConfigPreviewContext).
// La carte vit dans un portail à part du mask, un clic dessus ne ferme donc
// pas le Drawer.
// `viewport` : taille de la fenêtre, suivie par ConfigDrawer qui en déduit aussi
// la largeur du Drawer.
const ConfigPagePreview = ({ tabKey, values, left, viewport }) => {
  const landscape = useStore(({ landscape }) => landscape);
  const configuration = useStore(({ configuration }) => configuration);
  const header = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.HEADER.id));
  const footer = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.FOOTER.id));
  const preview = PREVIEWS[tabKey];
  if (!preview) return null;

  const width = landscape ? pageHeight : pageWidth;
  const height = landscape ? pageWidth : pageHeight;
  const margins = marginsObject(configuration);
  const layout = { width, height, margins, contentWidth: width - margins.left - margins.right };
  const { Body, Overlay } = preview;

  return (
    <ConfigPreviewFrame left={left} viewport={viewport} width={width} height={height}>
      {/* Page à l'échelle réelle, réduite pour tenir dans la carte. Vrais
          composants en lecture seule : aucune interaction possible. Même
          empilement que l'aperçu du document (PreviewOverlay) : en-tête,
          contenu espacé de la marge entre les lignes, pied de page. */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          color: configuration.baseFontColor?.style,
        }}
      >
        <PreviewProvider>
          <Container id={header.id} fromViewer={viewerTypes.PREVIEW} />
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              gap: configuration.lineSpacing,
              // Police en cours d'essai dans l'onglet Police, sinon celle du
              // document (cf. Container).
              fontFamily: values.fontFamily ?? configuration.fontFamily,
              fontSize: configuration.fontSize,
            }}
          >
            <Body layout={layout} values={values} />
          </div>
          <Container id={footer.id} fromViewer={viewerTypes.PREVIEW} />
          {Overlay && <Overlay layout={layout} />}
        </PreviewProvider>
      </div>
    </ConfigPreviewFrame>
  );
};

export default ConfigPagePreview;
